import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faPen, faSquarePlus, faSquareXmark, faTrash} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';

import {ClusterService} from '../../../services/cluster.service';
import {AuthService} from '../../../services/auth.service';

@Component({
    selector: 'app-cluster-form',
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule],
    templateUrl: './cluster-form.component.html',
    styleUrl: './cluster-form.component.css'
})
export class ClusterFormComponent implements OnInit {
  clusterForm: FormGroup;
  valueForm: FormGroup;
  clusterId: number | null = null;
  isEditMode: boolean = false;
  isAdminUser: boolean = false;
  clusterValues: any[] = [];
  isValueEdit: boolean = false;
  editingValueId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private clusterService: ClusterService,
    private auth: AuthService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    library: FaIconLibrary
  ) {
    library.addIcons(faTrash, faPen, faSquarePlus, faSquareXmark);
    this.clusterForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      is_active: [true]
    });
    this.valueForm = this.fb.group({
      value: ['', Validators.required],
      sort_order: [0],
      is_active: [true]
    });
  }

  ngOnInit(): void {
    const roles = this.getUserRoles();
      const isPlatformSuperuser = this.auth.isSuperUser();
      this.isAdminUser = roles.includes('Admin') && !isPlatformSuperuser;
      if (isPlatformSuperuser) {
      this.router.navigate(['/dashboard']);
      return;
    }
    if (!this.isAdminUser) {
      this.router.navigate(['/visitor-management/select-register']);
      return;
    }

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.clusterId = Number(params['id']);
        this.isEditMode = true;
        this.loadCluster(this.clusterId);
      }
      this.updateValueFormState();
    });
  }

  private getUserRoles(): string[] {
    const userInfo = this.auth.getCurrentUserInfo();
    const role = userInfo?.role;
    if (Array.isArray(role)) {
      return role;
    }
    return role ? [role] : [];
  }

  loadCluster(clusterId: number): void {
    this.clusterService.getCluster(clusterId).subscribe({
      next: (res: any) => {
        this.clusterForm.patchValue({
          name: res.name,
          description: res.description,
          is_active: res.is_active
        });
        this.loadClusterValues(clusterId);
      }
    });
  }

  loadClusterValues(clusterId: number): void {
    this.clusterService.listClusterValues(clusterId, 1, 200).subscribe({
      next: (res: any) => {
        this.clusterValues = res?.results || [];
      }
    });
  }

  submitCluster(): void {
    if (this.clusterForm.invalid) {
      this.clusterForm.markAllAsTouched();
      return;
    }
    const payload = this.clusterForm.value;
    if (this.isEditMode && this.clusterId) {
      this.clusterService.updateCluster(this.clusterId, payload).subscribe({
        next: () => {
          this.toastr.success('Cluster updated.', 'Success');
        }
      });
    } else {
      this.clusterService.createCluster(payload).subscribe({
        next: (res: any) => {
          this.toastr.success('Cluster created.', 'Success');
          this.router.navigate([`/visitor-management/clusters/${res.id}/edit`]);
        }
      });
    }
  }

  submitValue(): void {
    if (!this.clusterId) {
      this.toastr.error('Save cluster before adding values.', 'Error');
      return;
    }
    if (this.valueForm.invalid) {
      this.valueForm.markAllAsTouched();
      return;
    }
    const payload = {
      ...this.valueForm.value,
      cluster: this.clusterId
    };
    if (this.isValueEdit && this.editingValueId) {
      this.clusterService.updateClusterValue(this.editingValueId, payload).subscribe({
        next: () => {
          this.toastr.success('Value updated.', 'Success');
          this.resetValueForm();
          this.loadClusterValues(this.clusterId as number);
        }
      });
    } else {
      this.clusterService.createClusterValue(payload).subscribe({
        next: () => {
          this.toastr.success('Value added.', 'Success');
          this.resetValueForm();
          this.loadClusterValues(this.clusterId as number);
        }
      });
    }
  }

  editValue(value: any): void {
    this.isValueEdit = true;
    this.editingValueId = value.id;
    this.valueForm.patchValue({
      value: value.value,
      sort_order: value.sort_order,
      is_active: value.is_active
    });
  }

  deleteValue(valueId: number): void {
    this.clusterService.deleteClusterValue(valueId).subscribe({
      next: () => {
        this.toastr.success('Value deleted.', 'Success');
        if (this.clusterId) {
          this.loadClusterValues(this.clusterId);
        }
      }
    });
  }

  resetValueForm(): void {
    this.valueForm.reset({ value: '', sort_order: 0, is_active: true });
    this.isValueEdit = false;
    this.editingValueId = null;
    this.updateValueFormState();
  }

  cancel(): void {
    this.router.navigate(['/visitor-management/clusters']);
  }

  private updateValueFormState(): void {
    if (this.clusterId) {
      this.valueForm.enable({ emitEvent: false });
    } else {
      this.valueForm.disable({ emitEvent: false });
    }
  }
}
